"use client"

import { useEffect, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { sendGTMEvent } from "@next/third-parties/google"

import { generatePageTypeTrackingData } from "@/lib/gtmTrackingHelper"
import type { CommonPageContext } from "@/lib/tracking/tracking"

export interface ContentPageTrackingClientProps {
  commonPageContext?: CommonPageContext
  page_type: string
  sbLocale?: string
}

export default function ContentPageTrackingClient(props: ContentPageTrackingClientProps) {
  const [tracked, setTracked] = useState(false)
  const [clientContext, setClientContext] = useState<CommonPageContext | undefined>(props.commonPageContext)
  const { usedGuidedSelling, guidedSellingCampaign } = useUbStorefrontState()

  useEffect(() => {
    if (props.commonPageContext || clientContext) {
      return
    }

    const controller = new AbortController()
    const contextUrl = props.sbLocale
      ? `/api/storefront/context?sbLocale=${encodeURIComponent(props.sbLocale)}`
      : "/api/storefront/context"

    fetch(contextUrl, {
      cache: "no-store",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : undefined))
      .then((context: CommonPageContext | undefined) => {
        if (context) {
          setClientContext(context)
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          console.error("Could not load storefront context for content page tracking", error)
        }
      })

    return () => controller.abort()
  }, [clientContext, props.commonPageContext, props.sbLocale])

  useEffect(() => {
    const commonPageContext = props.commonPageContext ?? clientContext

    if (!tracked && commonPageContext) {
      sendGTMEvent(
        generatePageTypeTrackingData(
          commonPageContext.isAnonymous,
          commonPageContext.isB2B,
          props.page_type,
          commonPageContext.userId,
          undefined,
          undefined,
          usedGuidedSelling,
          guidedSellingCampaign
        )
      )

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTracked(true)
    }
  }, [clientContext, props.commonPageContext, props.page_type, tracked, usedGuidedSelling, guidedSellingCampaign])

  // eslint-disable-next-line better-tailwindcss/no-unknown-classes
  return <span className="PageType hidden">{props.page_type}</span>
}
